import React, { useMemo, useState } from 'react';
import { usePriceEditions } from '../../hooks/usePriceEditions';
import { useUserPermissions } from '../../hooks/useUserPermissions.jsx';
import { diffSheets } from '../../pricing/editionDiff';

const VENDOR = 'AIC';

export function EditionsPage({ user }) {
  // history: true — this page renders the full edition list (diff needs a
  // previous edition to compare against, revert needs more than the latest
  // row to target). The rep-facing configurator (App.js) keeps the default
  // limit: 1 so no rep pays for a 50-edition fetch.
  const { editions, status, publish, revert, refresh } = usePriceEditions(VENDOR, { history: true });
  const { userGroups } = useUserPermissions(user);
  const canPublish = userGroups.includes('catalogAuthor');
  const [diffFor, setDiffFor] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [excused, setExcused] = useState([]);

  const diffRows = useMemo(() => {
    if (!diffFor) return [];
    const idx = editions.findIndex((e) => e.editionNumber === diffFor);
    return diffSheets(editions[idx + 1]?.sheet ?? null, editions[idx]?.sheet ?? {});
  }, [diffFor, editions]);

  const onPublish = async () => {
    setBusy(true);
    setActionError(null);
    setExcused([]);
    // Quarter coverage is the hook's business (publishQuarters) — this page
    // must not import productDb.
    try {
      const record = await publish({ publishedBy: user?.username });
      setExcused(record?.coverageExcused ?? []);
    } catch (e) {
      // A rejection here is realistic, not hypothetical — publish is gated to
      // catalogAuthor, so an @auth denial (or a network/validation failure)
      // is one bad session away. Surface it rather than let the button just
      // re-enable with nothing to show for it.
      console.error('Failed to publish price edition:', e);
      setActionError(e?.message || 'Publish failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const onRevert = async (target) => {
    if (!window.confirm(`Revert to #${target.editionNumber}? This publishes a new edition.`)) return;
    setBusy(true);
    setActionError(null);
    setExcused([]);
    try {
      await revert(target, { publishedBy: user?.username });
    } catch (e) {
      console.error('Failed to revert price edition:', e);
      setActionError(e?.message || 'Revert failed. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  if (status === 'loading') return <div>Loading editions…</div>;

  // Distinguish "fetch failed, no cache to fall back to" from a vendor that
  // genuinely has zero published editions — both would otherwise render as
  // the same empty table. Retry reuses the hook's own refresh() rather than
  // adding a second loading affordance (matches ExecPricingView's status ===
  // 'error' + retry pattern for the same hook family).
  if (status === 'error') {
    return (
      <div>
        <h1 className="text-xl font-bold mb-4">Editions — {VENDOR}</h1>
        <div className="text-red-400 mb-2">Failed to load price editions.</div>
        <button
          type="button"
          onClick={refresh}
          className="px-3 py-1 rounded border border-gray-600 hover:bg-gray-800"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-xl font-bold">Editions — {VENDOR}</h1>
        {canPublish && (
          <button disabled={busy} onClick={onPublish}
                  className="bg-orange-600 disabled:opacity-50 px-3 py-1 rounded">
            Publish current catalog
          </button>
        )}
      </div>

      {actionError && <div className="text-red-400 mb-3 whitespace-pre-line">{actionError}</div>}

      {excused.length > 0 && (
        <div className="text-amber-400 mb-3 whitespace-pre-line">
          {`Published. ${excused.length} SKU-quarter price(s) were excused by an 'unpriced' ` +
           `terminator and carry no price in this edition:\n` +
           excused.map((e) => `  ${e.sku} @ ${e.quarter} — via ${e.via}`).join('\n')}
        </div>
      )}

      <table className="w-full text-sm">
        <thead className="text-gray-400 text-left">
          <tr><th>#</th><th>Published</th><th>By</th><th>Note</th><th /></tr>
        </thead>
        <tbody>
          {editions.map((e) => (
            <tr key={e.id} className="border-t border-gray-800">
              <td>#{String(e.editionNumber).padStart(4, '0')}</td>
              <td>{e.publishedAt}</td>
              <td>{e.publishedBy ?? '—'}</td>
              <td>{e.sourceNote ?? '—'}</td>
              <td className="text-right">
                <button className="underline mr-3" onClick={() => setDiffFor(e.editionNumber)}>
                  diff
                </button>
                {canPublish && e.editionNumber !== editions[0]?.editionNumber && (
                  <button className="underline" disabled={busy} onClick={() => onRevert(e)}>
                    revert
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {diffFor && (
        <div className="mt-6">
          <h2 className="font-bold mb-2">
            Movement in #{String(diffFor).padStart(4, '0')} ({diffRows.length} rows)
          </h2>
          <table className="w-full text-sm">
            <thead className="text-gray-400 text-left">
              <tr><th>SKU</th><th>Quarter</th><th>Was</th><th>Now</th><th>Δ</th></tr>
            </thead>
            <tbody>
              {diffRows.map((r) => (
                <tr key={`${r.productSku}/${r.quarter}`} className="border-t border-gray-800">
                  <td>{r.productSku}</td><td>{r.quarter}</td>
                  <td>{r.was ?? '—'}</td><td>{r.now ?? '—'}</td><td>{r.deltaDollars}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default EditionsPage;
